import React from 'react';
import PrototypeShell from './PrototypeShell';
import {
  Compass,
  CheckCircle,
  Circle,
  Calendar,
  ChatCircleDots,
  ArrowRight,
  Sparkle,
  Target,
  BookOpen,
  Envelope,
  Question,
  GraduationCap,
  UserCircle,
  Bell,
} from '@phosphor-icons/react';

const participant = {
  first_name: 'Maya',
  coach_first_name: 'Marcus',
  cohort_name: 'Spring 2026 · Career Restart',
  program_start: 'February 3, 2026',
  days_active: 12,
};

const readinessSteps = [
  { id: 1, label: 'Welcome & Consent', status: 'done' },
  { id: 2, label: 'Profile', status: 'done' },
  { id: 3, label: 'Financial Readiness Assessment', status: 'done' },
  { id: 4, label: 'Your Personalized Action Plan', status: 'current' },
  { id: 5, label: 'First Coach Check-in', status: 'upcoming' },
];

const actionPlan = [
  {
    id: 'a1',
    title: 'Build a starter emergency fund of $500',
    detail: 'Weekly auto-transfer of $25 to a separate savings account.',
    status: 'in_progress',
    progress: 25,
    saved: 125,
    target: 500,
  },
  {
    id: 'a2',
    title: 'Understand your first paycheck',
    detail: 'Complete the "Reading a Paycheck" module before your first payday.',
    status: 'todo',
    progress: 0,
  },
  {
    id: 'a3',
    title: 'Track expenses for 2 weeks',
    detail: 'Use the Spending Tracker to log purchases so we can spot easy wins.',
    status: 'todo',
    progress: 0,
  },
];

const reminders = [
  {
    id: 'r1',
    channel: 'Email',
    title: 'Check-in with Coach Marcus',
    when: 'Tomorrow · 2:00 PM',
    detail: '30-min video call · agenda: review action plan',
    featured: true,
  },
  {
    id: 'r2',
    channel: 'In-App',
    title: 'Weekly progress reflection',
    when: 'Friday',
    detail: 'A 3-minute prompt to note what worked and what didn\'t this week.',
    featured: false,
  },
];

const StepIcon = ({ status }) => {
  if (status === 'done')
    return <CheckCircle size={22} weight="fill" className="text-signal-success" />;
  if (status === 'current')
    return <Sparkle size={22} weight="fill" className="text-brand-gold" />;
  return <Circle size={22} className="text-ink-faint" />;
};

const WorkforceParticipantDashboard = () => {
  return (
    <PrototypeShell
      title="Participant Welcome Dashboard"
      subtitle="First screen after onboarding · shown every time the participant signs in"
    >
      {/* Greeting */}
      <section className="mb-8">
        {/* Warm personalized welcome banner */}
        <div className="mb-6 rounded-2xl bg-gradient-to-r from-brand-navy to-brand-navy-elevated text-white p-6 shadow-fluent-md">
          <div className="flex items-start gap-3">
            <Sparkle size={22} weight="fill" className="text-brand-gold flex-shrink-0 mt-1" />
            <div>
              <div className="font-heading text-xl leading-tight" data-testid="welcome-banner">
                Welcome back, {participant.first_name}! You&apos;re making great progress toward your financial goals.
              </div>
              <p className="text-white/70 text-sm mt-2 max-w-2xl">
                Complete your action plan below to prepare for your first paycheck and your upcoming coaching
                session with {participant.coach_first_name}.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold mb-1">
              {participant.cohort_name}
            </div>
            <h2 className="font-heading text-3xl text-ink" data-testid="participant-greeting">
              Hi, {participant.first_name}.
            </h2>
            <p className="text-ink-muted mt-2 max-w-xl">
              You&apos;re {participant.days_active} days into the program. Here&apos;s what to focus on this week.
              Your coach, {participant.coach_first_name}, can only see the pieces you&apos;ve chosen to share —
              <a href="#shared-info" className="text-brand-navy underline ml-1 hover:text-brand-gold">
                see what&apos;s shared
              </a>.
            </p>
          </div>
          <div className="bg-white rounded-xl border border-surface-border p-4 min-w-[220px] shadow-fluent-sm">
            <div className="text-[11px] uppercase tracking-widest text-ink-soft font-semibold">
              Program readiness
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="font-heading text-3xl text-brand-navy">64%</span>
              <span className="text-xs text-signal-success font-semibold">↑ 8 this week</span>
            </div>
            <div className="h-2 bg-surface-muted rounded-full mt-3 overflow-hidden">
              <div className="h-full bg-brand-gold rounded-full" style={{ width: '64%' }} />
            </div>
          </div>
        </div>
      </section>

      {/* Onboarding stepper */}
      <section className="mb-10 bg-white rounded-2xl border border-surface-border shadow-fluent-sm p-6">
        <div className="flex items-center gap-2 mb-5">
          <Compass size={18} weight="duotone" className="text-brand-navy" />
          <h3 className="font-heading text-lg text-ink">Your journey so far</h3>
        </div>
        <ol className="grid md:grid-cols-5 gap-3">
          {readinessSteps.map((s) => (
            <li
              key={s.id}
              className={`rounded-xl border p-4 ${
                s.status === 'current'
                  ? 'border-brand-gold bg-brand-gold/5'
                  : 'border-surface-border bg-surface-subtle'
              }`}
              data-testid={`onboarding-step-${s.id}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <StepIcon status={s.status} />
                <span className="text-[11px] uppercase tracking-widest text-ink-soft font-semibold">
                  Step {s.id}
                </span>
              </div>
              <div className="text-sm font-semibold text-ink leading-snug">{s.label}</div>
              <div className="text-[11px] text-ink-soft mt-1 capitalize">{s.status.replace('_', ' ')}</div>
            </li>
          ))}
        </ol>
      </section>

      {/* Two-column: Action plan + Reminders */}
      <section className="grid lg:grid-cols-3 gap-6 mb-10">
        {/* Action plan */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-surface-border shadow-fluent-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Target size={18} weight="duotone" className="text-brand-navy" />
              <h3 className="font-heading text-lg text-ink">Your action plan</h3>
            </div>
            <span className="text-[11px] uppercase tracking-widest text-ink-soft font-semibold">
              Generated Feb 8 · v1
            </span>
          </div>
          <div className="space-y-4">
            {actionPlan.map((a) => (
              <div
                key={a.id}
                className="border border-surface-border rounded-xl p-4 hover:border-brand-gold transition"
                data-testid={`action-item-${a.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="font-semibold text-ink">{a.title}</div>
                    <p className="text-sm text-ink-muted mt-1">{a.detail}</p>
                  </div>
                  <span
                    className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full whitespace-nowrap ${
                      a.status === 'in_progress'
                        ? 'bg-signal-info-soft text-signal-info'
                        : 'bg-surface-muted text-ink-soft'
                    }`}
                  >
                    {a.status === 'in_progress' ? 'In progress' : 'To do'}
                  </span>
                </div>
                {a.progress > 0 && (
                  <div className="mt-3">
                    <div className="flex justify-between items-baseline mb-1 text-xs">
                      <span className="text-ink-muted">
                        {a.saved != null && a.target != null ? (
                          <>
                            <span className="font-semibold text-ink">${a.saved}</span> of ${a.target} saved
                          </>
                        ) : (
                          'Progress'
                        )}
                      </span>
                      <span className="font-semibold text-brand-navy">{a.progress}% complete</span>
                    </div>
                    <div className="h-2 bg-surface-muted rounded-full overflow-hidden">
                      <div className="h-full bg-brand-gold" style={{ width: `${a.progress}%` }} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <button
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-navy hover:text-brand-gold transition"
            data-testid="view-full-action-plan"
          >
            View full plan <ArrowRight size={14} weight="bold" />
          </button>
        </div>

        {/* Reminders + Coach */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border-2 border-brand-gold shadow-fluent-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar size={18} weight="duotone" className="text-brand-navy" />
                <h3 className="font-heading text-lg text-ink">This week</h3>
              </div>
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-brand-navy bg-brand-gold px-2 py-1 rounded-full">
                <Bell size={11} weight="fill" /> Upcoming
              </span>
            </div>
            <ul className="space-y-4">
              {reminders.map((r) => (
                <li
                  key={r.id}
                  className={`rounded-xl p-3 ${
                    r.featured
                      ? 'bg-brand-gold/10 border border-brand-gold/40'
                      : 'border-l-2 border-brand-gold pl-3'
                  }`}
                  data-testid={`reminder-${r.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {r.channel === 'Email' ? (
                      <Envelope size={12} className="text-ink-soft" />
                    ) : (
                      <ChatCircleDots size={12} className="text-ink-soft" />
                    )}
                    <span className="text-[10px] uppercase tracking-widest text-ink-soft font-semibold">
                      {r.channel} · {r.when}
                    </span>
                  </div>
                  <div className={`font-semibold text-ink ${r.featured ? 'text-base' : 'text-sm'}`}>
                    {r.title}
                  </div>
                  <p className="text-xs text-ink-muted mt-1">{r.detail}</p>
                  {r.featured && (
                    <button
                      className="mt-3 text-xs font-semibold text-brand-navy inline-flex items-center gap-1 hover:text-brand-gold transition"
                      data-testid="add-to-calendar-button"
                    >
                      Add to calendar <ArrowRight size={12} weight="bold" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-ink-faint mt-4 leading-relaxed">
              You&apos;ll receive email reminders. SMS is coming soon — you can opt-in from
              <span className="whitespace-nowrap"> Profile → Notifications</span>.
            </p>
          </div>

          <div className="bg-brand-navy text-white rounded-2xl p-6 shadow-fluent-md">
            <div className="text-[11px] uppercase tracking-widest text-brand-gold font-semibold mb-2">
              Your coach
            </div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-brand-gold/20 border border-brand-gold flex items-center justify-center font-heading text-brand-gold">
                M
              </div>
              <div>
                <div className="font-semibold">{participant.coach_first_name} Alvarez</div>
                <div className="text-xs text-white/60">Career Navigator</div>
              </div>
            </div>
            <button
              className="mt-4 w-full bg-brand-gold text-brand-navy font-semibold rounded-lg py-2 hover:bg-brand-gold-hover transition"
              data-testid="message-coach-button"
            >
              Message Coach {participant.coach_first_name}
            </button>
            <p className="text-[10px] text-white/50 mt-3 leading-relaxed">
              Coach visibility: <span className="text-brand-gold">Metadata only</span> · Marcus can see your
              progress and engagement, not your financial details.
            </p>
          </div>
        </div>
      </section>

      {/* Learning strip */}
      <section id="shared-info" className="bg-white rounded-2xl border border-surface-border shadow-fluent-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen size={18} weight="duotone" className="text-brand-navy" />
          <h3 className="font-heading text-lg text-ink">Recommended for you</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { t: 'Reading your first paycheck', d: '8 min · Video', tag: 'Next up' },
            { t: 'Emergency fund basics', d: '6 min · Article', tag: '' },
            { t: 'Setting a realistic budget', d: '12 min · Interactive', tag: '' },
          ].map((c, i) => (
            <div
              key={i}
              className="border border-surface-border rounded-xl p-4 hover:border-brand-gold hover:shadow-fluent-sm transition"
            >
              {c.tag && (
                <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-gold bg-brand-gold/10 px-2 py-0.5 rounded-full">
                  {c.tag}
                </span>
              )}
              <div className="font-semibold text-ink mt-2">{c.t}</div>
              <div className="text-xs text-ink-soft mt-1">{c.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Help & Resources */}
      <section className="mt-6 bg-white rounded-2xl border border-surface-border shadow-fluent-sm p-6" data-testid="help-resources-section">
        <div className="flex items-center gap-2 mb-4">
          <Question size={18} weight="duotone" className="text-brand-navy" />
          <h3 className="font-heading text-lg text-ink">Help &amp; Resources</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              icon: UserCircle,
              title: 'Contact your coach',
              detail: `Message ${participant.coach_first_name} directly or schedule a check-in.`,
              cta: 'Open messages',
              testId: 'help-contact-coach',
            },
            {
              icon: GraduationCap,
              title: 'Learning Center',
              detail: 'Modules on paychecks, budgeting, credit, and savings.',
              cta: 'Browse lessons',
              testId: 'help-learning-center',
            },
            {
              icon: Question,
              title: 'FAQs',
              detail: 'Answers about your data, privacy, and how the program works.',
              cta: 'View FAQs',
              testId: 'help-faqs',
            },
          ].map((h) => {
            const Icon = h.icon;
            return (
              <button
                key={h.title}
                className="group text-left border border-surface-border rounded-xl p-4 hover:border-brand-gold hover:shadow-fluent-sm transition flex flex-col"
                data-testid={h.testId}
              >
                <div className="w-9 h-9 rounded-lg bg-brand-navy/5 text-brand-navy flex items-center justify-center mb-3 group-hover:bg-brand-gold/10 transition">
                  <Icon size={18} weight="duotone" />
                </div>
                <div className="font-semibold text-ink text-sm">{h.title}</div>
                <p className="text-xs text-ink-muted mt-1 flex-1">{h.detail}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-navy group-hover:text-brand-gold transition">
                  {h.cta} <ArrowRight size={12} weight="bold" />
                </span>
              </button>
            );
          })}
        </div>
      </section>
    </PrototypeShell>
  );
};

export default WorkforceParticipantDashboard;
